import type { AccountData } from "./mcp-registration";
import { paths } from "@/config/paths";
import useQueryParams from "@/hooks/use-query-params";
import { useDataset } from "@/features/datasets/api/get-dataset";
import { cn } from "@/lib/utils";
import { Button } from "@blueskydataplatform/shared-ui/components/ui/button";
import { usePlans } from "@blueskydataplatform/shared-ui/features/plans/api/get-plans";
import type { Plan } from "@blueskydataplatform/shared-ui/types/plans.types";
import type { DatasetPlan } from "@blueskydataplatform/shared-ui/types/datasets.types";
import { useContactUs } from "@blueskydataplatform/shared-ui/modules/enterprise/enterpise-contact-provider";
import { PlanCard } from "./plan-card";
import { ArrowRight, Check, Mail, MessageSquare, Sparkles, Zap } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import Logo from "@/components/logo";
import McpAccessDestination from "@/features/login/mcp-access-destination";
import { GENERIC_CLIENT, useMcpClientName } from "@/features/login/mcp-client-name";
import { usePreserveQuery } from "@/hooks/use-preserve-query";
import { useOrder } from "@blueskydataplatform/shared-ui/store/use-order";
import { getPublicFreeTrialDays } from "@blueskydataplatform/shared-ui/config/public-runtime-config";

const DATASET_MT_NEWSWIRES_GLOBAL = "MT_NEWSWIRES_Global";
const DATASET_MT_NEWSWIRES_NORTH_AMERICA = "MT_NEWSWIRES_North_America";
const freeTrialDays = getPublicFreeTrialDays();
const MT_NEWSWIRES_CONFIG = {
  providerName: "MT Newswires",
  description: (
    <>
      Your MT Newswires account is ready. Once connected, you can access up-to-date financial news for US and global
      companies in Claude. To enable market data (prices and fundamentals), reconnect in Claude via{" "}
      <strong>Switch Provider</strong> → viaNexus.
    </>
  ),
  freeTrialFeatures: [
    "News coverage for unlimited symbols",
    "Access to Global News (via MT Newswires Global) and MT Newswires US",
    `${freeTrialDays}-day free trial, no credit card required`,
    'Try in Claude: "What is the latest news for Microsoft?"',
  ],
  infoBlock: {
    leadIn: "One more thing —",
    title: "Unlock news + market data in Claude",
    valueProp: "Combine real-time news with prices and fundamentals so Claude can give you the full picture.",
    description: "One-time setup so Claude can use both MT Newswires and CORE data (prices, fundamentals):",
    steps: [
      "Disconnect your MT Newswires connection in Claude",
      "Reconnect using Switch Provider → choose viaNexus",
      "Sign in with your viaNexus email and password",
    ],
  },
  supportEmail: "support@viaNexus.com",
} as const;

type NewsScope = "us" | "global" | "both";

/** Callback when user selects a plan. Fill in to handle subscription flow. newsScope is the selected US/Global/Both option. */
const onPlanSelect = (plan: Plan, _newsScope: NewsScope) => {
  void plan;
  void _newsScope; // TODO: handle plan selection (e.g. redirect to checkout, open modal)
};

const MTNewswiresRegistration = ({ accountData, onFinish }: { accountData: AccountData; onFinish: () => void }) => {
  const cfg = MT_NEWSWIRES_CONFIG;
  const providerName = accountData.provider?.name;
  const contactUsData = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { orgType, provider, ...contactUsData } = accountData;
    return contactUsData;
  }, [accountData]);
  const { plan: selectedPlan, addPlan, addItem, clearCart } = useOrder();
  const preserveQueryFn = usePreserveQuery();
  const openContactUs = useContactUs();
  const CONSOLE_DASHBOARD_HREF = "/console";
  const CONSOLE_SUBSCRIBE_HREF = "/console/my-subscription/checkout";
  const CATALOG_PLANS_HREF = "/console/marketplace";
  const isBusiness = accountData.orgType === "business";
  const { callbackUrl, state } = useQueryParams();
  const clientName = useMcpClientName(state, callbackUrl) ?? GENERIC_CLIENT;

  const [newsScope, setNewsScope] = useState<NewsScope>("both");

  const { data: plans, isLoading: isLoadingPlans } = usePlans({ data: { useCase: "personal" } });
  const { data: datasetGlobal } = useDataset({
    id: DATASET_MT_NEWSWIRES_GLOBAL,
    useCase: accountData.orgType === "business" ? "commercial" : "personal",
  });
  const { data: datasetNorthAmerica } = useDataset({
    id: DATASET_MT_NEWSWIRES_NORTH_AMERICA,
    useCase: accountData.orgType === "business" ? "commercial" : "personal",
  });

  const paidPlans = useMemo(() => (plans ?? []).filter(p => !p.isFree && p.id !== "enterprise"), [plans]);

  const getDatasetPriceForPlan = useMemo(() => {
    const priceForPlan = (datasetPlans: DatasetPlan[] | undefined, planId: string): number | null => {
      const p = datasetPlans?.find(dp => dp.planId === planId);
      return p != null && p.active ? p.monthlyPrice : null;
    };
    return (planId: string): number | null => {
      const us = priceForPlan(datasetNorthAmerica?.plans, planId);
      const global = priceForPlan(datasetGlobal?.plans, planId);
      if (newsScope === "us") return us;
      if (newsScope === "global") return global;
      if (newsScope === "both") {
        const a = us ?? 0;
        const b = global ?? 0;
        return a + b > 0 ? a + b : null;
      }
      return null;
    };
  }, [newsScope, datasetGlobal?.plans, datasetNorthAmerica?.plans]);

  const handlePlanSelect = (plan: Plan) => {
    clearCart();
    addPlan(plan);
    if (newsScope === "us" || newsScope === "both") {
      if (datasetNorthAmerica) addItem(datasetNorthAmerica);
    }
    if (newsScope === "global" || newsScope === "both") {
      if (datasetGlobal) addItem(datasetGlobal);
    }
    onPlanSelect(plan, newsScope);
  };

  const handleFreeTrialClick = () => {
    void Promise.resolve(onFinish()).catch(() => undefined);
  };

  const handleSubscribeClick = () => {
    void Promise.resolve(onFinish()).catch(() => undefined);
  };

  return (
    <div className="bg-background flex-1">
      {/* Hero */}
      <section className="hero-gradient pt-24 pb-12 md:pt-32 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center mb-6">
            <Logo className="mb-4" />
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 text-xs font-semibold uppercase tracking-wider px-4 py-2 mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            Account created
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-6">
            You&apos;re all set
          </h1>
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
            {cfg.description ?? (
              <>
                Your <strong>{providerName}</strong> account is ready. You can use it in Claude or other MCP clients.
              </>
            )}
          </p>
          <p className="mt-4 inline-flex items-center gap-2 rounded-lg bg-slate-100 dark:bg-slate-800/80 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400">
            <Mail className="w-4 h-4 shrink-0" />
            We&apos;ve sent you an email with full instructions.
          </p>
        </div>
      </section>

      {/* Choose your path — marketing section */}
      <section className="py-16 md:py-24 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-2">
              Choose how to get started
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-xl mx-auto">
              Start with the free trial or subscribe now for more coverage. Either one connects {clientName} to your MT
              Newswires account.
            </p>
            <McpAccessDestination className="mt-3 text-slate-600 dark:text-slate-400" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 max-w-5xl mx-auto">
            {/* Free trial card */}
            <div className="flex flex-col rounded-3xl border-2 border-emerald-200 dark:border-emerald-800 bg-gradient-to-b from-emerald-50/80 to-white dark:from-emerald-950/30 dark:to-slate-900 p-8 shadow-lg shadow-emerald-100/50 dark:shadow-none">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 text-xs font-bold uppercase tracking-wider px-3 py-1.5 w-fit mb-6">
                <Zap className="w-3.5 h-3.5" />
                Free
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Start with the free trial</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm mb-6">
                Get started in Claude right away. Upgrade anytime when you need more.
              </p>
              <ul className="space-y-3 mb-8">
                {cfg.freeTrialFeatures.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-700 dark:text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: feature }} />
                  </li>
                ))}
              </ul>
              <Button
                size="lg"
                onClick={handleFreeTrialClick}
                className="mt-auto rounded-xl font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md hover:shadow-lg transition-all"
              >
                <a href={`${CONSOLE_DASHBOARD_HREF}?mcpFreeTrial=true`} target="_blank" rel="noreferrer">
                  Allow and start free trial
                  <ArrowRight className="w-4 h-4 ml-2 inline-block" />
                </a>
              </Button>
              {!isBusiness && (
                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  You can subscribe to a plan later from your{" "}
                  <a
                    href={CONSOLE_DASHBOARD_HREF}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
                  >
                    Dashboard
                  </a>
                  .
                </p>
              )}
            </div>

            {/* Subscribe card */}
            <div className="flex flex-col rounded-3xl border-2 border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 p-8 shadow-lg">
              <div className="inline-flex items-center gap-2 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 text-xs font-bold uppercase tracking-wider px-3 py-1.5 w-fit mb-6">
                More coverage
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                Subscribe to a plan + MT Newswires Dataset
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
                Expand your coverage — more news and data for Claude.
              </p>

              {isBusiness ? (
                <div className="flex flex-col flex-1 min-h-0 pt-4">
                  <p className="text-slate-600 dark:text-slate-400 text-sm flex-1">
                    If you want to expand your coverage, contact us to discuss pricing and available plans for your
                    organization.
                  </p>
                  <Button
                    size="lg"
                    onClick={() => openContactUs(contactUsData)}
                    className="mt-auto w-full rounded-xl font-semibold shrink-0"
                  >
                    <MessageSquare className="w-4 h-4 mr-2 shrink-0" />
                    Contact us
                  </Button>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">News coverage</p>
                    <div
                      className="inline-flex p-1 rounded-xl bg-slate-200/80 dark:bg-slate-700/80 gap-0.5"
                      role="group"
                      aria-label="News coverage"
                    >
                      {(
                        [
                          { value: "us" as const, label: "North America News" },
                          { value: "global" as const, label: "Global News" },
                          { value: "both" as const, label: "Both" },
                        ] as const
                      ).map(({ value, label }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setNewsScope(value)}
                          className={cn(
                            "px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                            newsScope === value
                              ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm"
                              : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
                          )}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {isLoadingPlans ? (
                    <div className="space-y-3 flex-1">
                      {[1, 2, 3].map(i => (
                        <div
                          key={i}
                          className="h-20 rounded-xl bg-slate-200 dark:bg-slate-700 animate-pulse"
                          aria-hidden
                        />
                      ))}
                    </div>
                  ) : paidPlans.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400 py-4 flex-1">
                      No paid plans available.{" "}
                      <a href={CATALOG_PLANS_HREF} className="font-semibold text-primary hover:underline">
                        View plans in dashboard
                      </a>
                    </p>
                  ) : (
                    <>
                      <div className="space-y-3 flex-1 min-h-0">
                        {paidPlans.map(plan => (
                          <PlanCard
                            key={plan.id}
                            plan={plan}
                            datasetPrice={getDatasetPriceForPlan(plan.id)}
                            isSelected={selectedPlan?.id === plan.id}
                            onSelect={() => handlePlanSelect(plan)}
                          />
                        ))}
                      </div>
                      <Button
                        size="lg"
                        asChild
                        disabled={!selectedPlan}
                        onClick={() => selectedPlan && handleSubscribeClick()}
                        className="mt-4 w-full rounded-xl font-semibold"
                      >
                        <a href={`${CONSOLE_SUBSCRIBE_HREF}?mcpSubscribe=true`} target="_blank" rel="noreferrer">
                          Allow and subscribe
                          <ArrowRight className="w-4 h-4 ml-2 inline-block" />
                        </a>
                      </Button>
                    </>
                  )}

                  <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
                    You can change your plan anytime in your{" "}
                    <a href={CONSOLE_DASHBOARD_HREF} className="font-medium text-primary hover:underline">
                      dashboard
                    </a>
                    .
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Setup reminder */}
      {cfg.infoBlock && (
        <section className="py-12 bg-slate-50 dark:bg-slate-950">
          <div className="max-w-3xl mx-auto px-6">
            <div className="rounded-2xl bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-800/50 p-6 md:p-8">
              {cfg.infoBlock.leadIn && (
                <p className="text-sm font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-2">
                  {cfg.infoBlock.leadIn}
                </p>
              )}
              <h2 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {cfg.infoBlock.title}
              </h2>
              {"valueProp" in cfg.infoBlock && cfg.infoBlock.valueProp && (
                <p className="text-slate-600 dark:text-slate-400 mb-4">{cfg.infoBlock.valueProp}</p>
              )}
              {cfg.infoBlock.description && (
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">{cfg.infoBlock.description}</p>
              )}
              {cfg.infoBlock.steps && cfg.infoBlock.steps.length > 0 && (
                <ol className="list-decimal list-inside space-y-2 text-sm text-slate-700 dark:text-slate-300 font-medium">
                  {cfg.infoBlock.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Footer CTAs */}
      <section className="py-12 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="rounded-xl font-semibold">
              <a href={CONSOLE_DASHBOARD_HREF}>
                Go to Dashboard
                <ArrowRight className="w-5 h-5 ml-2 inline-block" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl font-semibold">
              <a href={CATALOG_PLANS_HREF}>Manage plan</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="ghost"
              className="rounded-xl font-semibold text-slate-600 dark:text-slate-400"
            >
              <Link to={preserveQueryFn(paths.home.getHref())} target="_self">
                Return to Sign In
              </Link>
            </Button>
          </div>
          <p className="mt-6 text-sm text-slate-500 dark:text-slate-400">
            Questions?{" "}
            <a
              href={`mailto:${cfg.supportEmail}`}
              className="font-semibold text-primary hover:underline underline-offset-2"
            >
              {cfg.supportEmail}
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default MTNewswiresRegistration;
